import { Injectable, signal, computed } from '@angular/core';
import { Router } from '@angular/router';

export interface DownloadFile {
  id: string;
  fileName: string;
  status: 'InProgress' | 'Completed' | 'Failed';
  downloadUrl?: string;
  jobId: number;
}

export interface DownloadStatus {
  isVisible: boolean;
  isCollapsed: boolean;
  title: string;
  files: DownloadFile[];
}

@Injectable({
  providedIn: 'root'
})
export class DownloadStatusService {
  // Global signal to control download status
  private downloadStatusSignal = signal<DownloadStatus>({
    isVisible: false,
    isCollapsed: true,
    title: 'Export Status',
    files: []
  });

  // Public computed signals
  downloadStatus = computed(() => this.downloadStatusSignal());
  isVisible = computed(() => this.downloadStatusSignal().isVisible);
  isCollapsed = computed(() => this.downloadStatusSignal().isCollapsed);
  title = computed(() => this.downloadStatusSignal().title);
  files = computed(() => this.downloadStatusSignal().files);

  // Check if any files are still in progress
  hasActiveDownloads = computed(() => 
    this.files().some(file => file.status === 'InProgress')
  );

  // Check if any files failed
  hasFailedDownloads = computed(() => 
    this.files().some(file => file.status === 'Failed')
  );

  constructor(private router: Router) {}

  /**
   * Update the entire download status
   */
  updateDownloadStatus(status: Partial<DownloadStatus>): void {
    this.downloadStatusSignal.update(current => ({ ...current, ...status }));
  }

  /**
   * Add a new file to the download queue
   */
  addFile(jobId: number, fileName: string): string {
    const fileId = this.generateId();
    const newFile: DownloadFile = {
      id: fileId,
      fileName: fileName,
      status: 'InProgress',
      jobId: jobId
    };
    
    console.log('Adding file to download status:', newFile);
    
    this.downloadStatusSignal.update(current => ({
      ...current,
      files: [newFile, ...current.files], // Add new files at the beginning
      isVisible: true
    }));

    console.log('Download status updated, isVisible:', this.downloadStatusSignal().isVisible);
    return fileId;
  }

  /**
   * Update the status of a specific file
   */
  updateFileStatus(jobId: number, status: 'InProgress' | 'Completed' | 'Failed', downloadUrl?: string): void {
    this.downloadStatusSignal.update(current => ({
      ...current,
      files: current.files.map(file => 
        file.jobId === jobId 
          ? { ...file, status, downloadUrl }
          : file
      )
    }));
  }

  /**
   * Remove a file from the download queue
   */
  removeFile(fileId: string): void {
    this.downloadStatusSignal.update(current => ({
      ...current,
      files: current.files.filter(file => file.id !== fileId)
    }));
  }

  /**
   * Toggle the collapse state of the download panel
   */
  toggleCollapse(): void {
    this.downloadStatusSignal.update(current => ({
      ...current,
      isCollapsed: !current.isCollapsed
    }));
  }

  /**
   * Close the download status panel
   */
  close(): void {
    this.downloadStatusSignal.update(current => ({
      ...current,
      isVisible: false
    }));
  }

  /**
   * Show the download status panel
   */
  show(): void {
    this.downloadStatusSignal.update(current => ({
      ...current,
      isVisible: true
    }));
  }

  /**
   * Clear all completed and failed files
   */
  clearCompletedFiles(): void {
    this.downloadStatusSignal.update(current => {
      const remainingFiles = current.files.filter(file => file.status === 'InProgress');
      return {
        ...current,
        files: remainingFiles,
        // Keep visible even if no files remain, let user manually close
        isVisible: current.isVisible
      };
    });
  }

  /**
   * Clear all files
   */
  clearAllFiles(): void {
    this.downloadStatusSignal.update(current => ({
      ...current,
      files: [],
      isVisible: false
    }));
  }

  /**
   * Clear all files but keep download status visible
   * This is used when jobs complete but we want to keep the component visible
   */
  clearFilesKeepVisible(): void {
    this.downloadStatusSignal.update(current => ({
      ...current,
      files: []
    }));
  }

  /**
   * Clear old completed/failed files (older than specified hours)
   * This helps prevent clutter from old export jobs
   */
  clearOldFiles(hoursOld: number = 24): void {
    const cutoffTime = new Date();
    cutoffTime.setHours(cutoffTime.getHours() - hoursOld);

    this.downloadStatusSignal.update(current => ({
      ...current,
      files: current.files.filter(file => {
        // Keep in-progress files
        if (file.status === 'InProgress') {
          return true;
        }
        
        return true;
      })
    }));
  }

  /**
   * Check if all files are completed or failed
   */
  areAllFilesCompleted(): boolean {
    return this.files().length > 0 && this.files().every(file => 
      file.status === 'Completed' || file.status === 'Failed'
    );
  }

  /**
   * Restore existing files from backend data
   * This is used when the app loads and there are existing export jobs
   */
  restoreExistingFiles(backendFiles: Array<{
    id: number;
    moduleName: string;
    status: string;
    downloadUrl: string;
    createdOn: string;
  }>): void {
    // Convert backend files to DownloadFile format, maintaining the order from API
    const existingFiles: DownloadFile[] = backendFiles.map(file => ({
      id: this.generateId(),
      fileName: file.moduleName,
      status: file.status as 'InProgress' | 'Completed' | 'Failed',
      downloadUrl: file.downloadUrl || undefined,
      jobId: file.id
    }));

    // Only show the download status if there are files to show
    if (existingFiles.length > 0) {
      this.downloadStatusSignal.update(current => ({
        ...current,
        files: existingFiles, // Maintain the order from API (most recent first)
        isVisible: true
      }));
      console.log('Restored existing files and showing download status:', existingFiles);
    } else {
      // Don't show download status if no files found on startup
      this.downloadStatusSignal.update(current => ({
        ...current,
        files: [],
        isVisible: false
      }));
      console.log('No existing files found, keeping download status hidden');
    }
  }

  /**
   * Generate unique ID for files
   */
  private generateId(): string {
    return 'file-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9);
  }


  viewMore(): void {
    this.router.navigate(['/export-files']);
  }
} 